import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { IconCheck, IconInfoCircle } from '@tabler/icons-react';

import Button from '../buttons/Button';
import SelectInput from '../inputs/SelectInput';

/**
 * Resolve the distinct values found in a CSV tags column to tag IDs.
 *
 * Visual spec: option A2, "exceptions-first reconcile"
 * (.kiro/specs/contact-grouping/mockups.html, surface A, middle panel).
 *
 * The layout grows with the number of PROBLEMS, not the number of values: values
 * that already match an existing tag collapse into a single summary line, and only
 * undecided values get a card. That is what makes a clean 4-value file and a messy
 * 50-value CRM export both readable — the alternative (a row per value) turns the
 * latter into a wall where four rows actually need a human.
 */
const TagValueResolution = ({
  tagsHeader,
  matched,
  unmatched,
  decisions,
  rowsWithNoValue,
  tags,
  onMap,
  onIgnore,
  onCreate,
}) => {
  const [showMatched, setShowMatched] = useState(false);

  const tagOptions = useMemo(
    () => [{ label: 'Choose a tag…', value: '' }, ...tags.map((tag) => ({ label: tag.name, value: tag.id }))],
    [tags],
  );

  const pending = unmatched.filter(({ value }) => {
    const decision = decisions[value];
    return !decision?.tagId && !decision?.ignored;
  });

  const decided = unmatched.filter(({ value }) => {
    const decision = decisions[value];
    return decision?.tagId || decision?.ignored;
  });

  const tagNameById = useMemo(() => new Map(tags.map((tag) => [tag.id, tag.name])), [tags]);

  return (
    <div className="rounded-lg border border-primary-100">
      <div className="border-b border-primary-100 bg-primary-50 px-4 py-3">
        <p className="font-semibold text-primary-700">Resolve tags from &ldquo;{tagsHeader}&rdquo;</p>
        <p className="text-sm text-primary-700 opacity-85">
          {matched.length + unmatched.length} distinct {matched.length + unmatched.length === 1 ? 'value' : 'values'}{' '}
          found in this column.
        </p>
      </div>

      <div className="space-y-3 p-4">
        {matched.length > 0 && (
          <div className="rounded-lg border border-green-100 bg-green-50 px-3.5 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <IconCheck size={16} className="shrink-0 text-green-600" />
                <p className="text-sm font-semibold text-green-600">
                  {matched.length} {matched.length === 1 ? 'value matches' : 'values match'} an existing tag
                </p>
              </div>
              <Button
                title={showMatched ? 'Hide' : 'Review'}
                size="sm"
                onClick={() => setShowMatched((shown) => !shown)}
              />
            </div>

            {showMatched && (
              <ul className="mt-2.5 space-y-1 border-t border-green-100 pt-2.5">
                {matched.map(({ value, rowCount, tag }) => (
                  <li key={value} className="text-sm text-green-600">
                    &ldquo;{value}&rdquo; → {tag.name}{' '}
                    <span className="opacity-75">
                      ({rowCount.toLocaleString()} {rowCount === 1 ? 'row' : 'rows'})
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {pending.length > 0 && (
          <p className="text-caption1 font-bold uppercase tracking-wide text-gray-500">
            Needs a decision · {pending.length}
          </p>
        )}

        {pending.map(({ value, rowCount }) => (
          <div key={value} className="rounded-lg border border-orange-200 bg-orange-50 p-3.5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold">&ldquo;{value}&rdquo;</span>
                  <span className="rounded-full bg-orange-50 px-2 py-0.5 text-sm font-semibold text-orange-600">
                    {rowCount.toLocaleString()} {rowCount === 1 ? 'row' : 'rows'}
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-orange-700">No tag with this name exists yet.</p>
              </div>

              <div className="flex shrink-0 flex-wrap items-center gap-2">
                <Button title="Create tag" color="primary" size="sm" onClick={() => onCreate(value)} />
                <SelectInput
                  id={`map-${value}`}
                  value=""
                  options={tagOptions}
                  onChange={(event) => onMap(value, event.target.value)}
                  containerClassName="min-w-[168px]"
                />
                <Button title="Ignore" size="sm" onClick={() => onIgnore(value)} />
              </div>
            </div>
          </div>
        ))}

        {decided.map(({ value, rowCount }) => {
          const decision = decisions[value];
          return (
            <div
              key={value}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-50 px-3.5 py-2.5"
            >
              <p className="text-sm">
                <span className="font-semibold">&ldquo;{value}&rdquo;</span>{' '}
                {decision.ignored ? (
                  <span className="text-gray-600">will be ignored</span>
                ) : (
                  <span className="text-green-600">→ {tagNameById.get(decision.tagId)}</span>
                )}{' '}
                <span className="text-gray-500">
                  ({rowCount.toLocaleString()} {rowCount === 1 ? 'row' : 'rows'})
                </span>
              </p>
              <Button title="Change" size="sm" onClick={() => onMap(value, '')} />
            </div>
          );
        })}

        {rowsWithNoValue > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-dashed border-gray-100 bg-gray-10 px-3.5 py-2.5">
            <IconInfoCircle size={16} className="shrink-0 text-gray-500" />
            <p className="text-sm text-gray-600">
              {rowsWithNoValue.toLocaleString()} {rowsWithNoValue === 1 ? 'row has' : 'rows have'} no value in this
              column and will not be tagged.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

TagValueResolution.propTypes = {
  tagsHeader: PropTypes.string.isRequired,
  matched: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      rowCount: PropTypes.number.isRequired,
      tag: PropTypes.shape({ id: PropTypes.string, name: PropTypes.string }).isRequired,
    }),
  ).isRequired,
  unmatched: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      rowCount: PropTypes.number.isRequired,
    }),
  ).isRequired,
  decisions: PropTypes.objectOf(PropTypes.shape({ tagId: PropTypes.string, ignored: PropTypes.bool })).isRequired,
  rowsWithNoValue: PropTypes.number.isRequired,
  tags: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })).isRequired,
  onMap: PropTypes.func.isRequired,
  onIgnore: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
};

export default TagValueResolution;
